import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MessengerService } from '../core/service/messenger.service';

@Injectable({
    providedIn: 'root'
})
export class TaskUtility implements OnDestroy
{
    public distinctPriorities: Array<{ priorityName: string; priority: string }> = [
        { priorityName: 'Low', priority: 'LOW' },
        { priorityName: 'Medium', priority: 'MEDIUM' },
        { priorityName: 'High', priority: 'HIGH' },
        { priorityName: 'Urgent', priority: 'URGENT' }
    ];

    public taskFilters: Array<{ text: string; value: string }> = [
        { text: 'Open', value: 'OPEN' },
        { text: 'Completed', value: 'COMPLETED' },
        { text: 'All', value: 'ALL' }
    ];

    /**
     * Calendar texts to use with moment translations
     */
    public calendarTexts: Array<{ text: string }> = [
        { text: 'Today' },
        { text: 'Tomorrow' },
        { text: 'Yesterday' },
        { text: 'Last' },
        { text: '{{value}} Days ago' },
        { text: 'In {{value}} Days' }
    ];

    private messagingLanguageChangedSubscription: Subscription;

    constructor(
        private translate: TranslateService,
        private messengerService: MessengerService)
    {
        this.loadLocalizations();
        this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
            next: (message) =>
            {
                if (message && message.type && message.type === 'languageChanged')
                {
                    this.loadLocalizations();
                }
            }
        });
    }

    public ngOnDestroy(): void
    {
        if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
    }

    /**
     * Calculates the badge class based on days left.
     *
     * @param daysLeft Number of days left before due date.
     */
    public daysLeftBadgeClass(daysLeft: number): string
    {
        return 'badge badge-pill ' + (daysLeft > 0 ? 'badge-secondary' : 'badge-danger');
    }

    /**
     * Return the localized priority to show.
     *
     * @param priority Priority
     */
    public priority(priority: string): string
    {
        const foundPriority = this.distinctPriorities.find(p => p.priority === priority.toUpperCase());
        return foundPriority ? foundPriority.priorityName : priority;
    }

    /**
     * Calculates the badge class based on priority.
     *
     * @param priority Task priority.
     */
    public priorityBadgeClass(priority: string): string
    {
        switch (priority)
        {
            case 'Low': return 'profileLetters bg-priority-low';
            case 'Medium': return 'profileLetters bg-priority-medium';
            case 'High': return 'profileLetters bg-priority-high';
            case 'Urgent': return 'profileLetters bg-priority-urgent';
            default: return 'profileLetters bg-priority-low';
        }
    }

    /**
     * Load localizations
     */
    private loadLocalizations(): void
    {
        this.translate.get(['LOW', 'MEDIUM', 'HIGH', 'URGENT']).subscribe({
            next: (res) =>
            {
                let index = 0;
                for (const key in res)
                {
                    if ({}.hasOwnProperty.call(res, key))
                    {
                        this.distinctPriorities[index].priorityName = res[key];
                        index++;
                    }
                }
            }
        });

        this.translate.get('LIST-FILTERS').subscribe({
            next: (res: string) =>
            {
                const splitted = res.split(',');
                for (let i = 0; i < splitted.length; i++)
                {
                    if (this.taskFilters.length > i)
                    {
                        this.taskFilters[i].text = splitted[i];
                    }
                }
            }
        });

        this.translate.get('MOMENT-CALENDAR').subscribe({
            next: (res) =>
            {
                const splitted = res.split(',');
                for (let i = 0; i < splitted.length; i++)
                {
                    this.calendarTexts[i].text = splitted[i];
                }
            }
        });
    }
}
